import { useEffect, useState } from 'react'
import { usePostHog } from '@posthog/react'
import ContentHeader from '../components/ContentHeader'
import { getSessionAuth } from '../utils/sessionAuth'
import { getBillingStatus, cancelSubscription, getStripePrices, createCheckoutSession } from '../api/user'
import { ensureSessionUserId } from '../utils/sessionUser'
import '../style/views/PaymentsView.css'

const STRIPE_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID as string | undefined

// Parse VITE_STRIPE_PRODUCT_ID — stored as a JSON array string e.g. '["prod_xxx"]'
const parseProductIds = (): string[] => {
  const raw = import.meta.env.VITE_STRIPE_PRODUCT_ID as string | undefined
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string')
    if (typeof parsed === 'string') return [parsed]
  } catch {
    if (typeof raw === 'string' && raw.trim()) return [raw.trim()]
  }
  return []
}
const STRIPE_PRODUCT_IDS = parseProductIds()

type StripePrice = {
  price_id: string
  product_id?: string
  currency?: string
  unit_amount: number | null
  interval: 'day' | 'week' | 'month' | 'year' | null
  interval_count?: number | null
  nickname?: string | null
  active?: boolean
  price_per_interval?: string | null
  trial_eligible?: boolean
}

const formatAmount = (price: StripePrice): string => {
  if (price.unit_amount == null) return 'Custom'
  const dollars = price.unit_amount / 100
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

const formatInterval = (price: StripePrice): string => {
  if (!price.interval) return ''
  const count = price.interval_count ?? 1
  if (count === 1) return `/ ${price.interval}`
  return `/ ${count} ${price.interval}s`
}

const intervalSortOrder: Record<string, number> = { month: 0, year: 1, week: 2, day: 3 }

type BillingStatusResponse = {
  hasCustomer?: boolean
  subscriptionStatus?: string | null
  currentPeriodEnd?: string | null
  cancelAtPeriodEnd?: boolean
  billingExempt?: boolean
  stripeCustomerId?: string | null
  stripePriceId?: string | null
  stripeProductId?: string | null
  livemode?: boolean
}

type PaymentsViewProps = {
  variant?: 'paywall' | 'billing'
  onLogout?: () => void
}

const statusLabel = (status: string | null | undefined) => {
  if (!status) return 'No subscription'
  return status
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')
}

const statusTone = (billing: BillingStatusResponse | null) => {
  if (!billing?.hasCustomer) return 'neutral'
  const s = billing.subscriptionStatus
  if (s === 'active' || s === 'trialing') return 'success'
  if (s === 'past_due' || s === 'unpaid' || s === 'canceled') return 'warning'
  return 'neutral'
}

// ─── Billing settings (in-app) ───────────────────────────────────────────────

function BillingView() {
  const posthog = usePostHog()
  const [billing, setBilling] = useState<BillingStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelImmediately, setCancelImmediately] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const userId = await ensureSessionUserId(getSessionAuth())
        if (!userId) throw new Error('Unable to identify your account.')
        const data = (await getBillingStatus(userId)) as BillingStatusResponse
        if (!cancelled) setBilling(data)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load billing status.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const handleCancelSubscription = async () => {
    setCancelling(true)
    setCancelError(null)
    try {
      const userId = await ensureSessionUserId(getSessionAuth())
      if (!userId) throw new Error('Unable to identify your account.')
      const updated = (await cancelSubscription(userId, cancelImmediately)) as BillingStatusResponse
      posthog?.capture('subscription_cancelled', {
        cancel_timing: cancelImmediately ? 'immediately' : 'at_period_end',
      })
      setBilling(updated)
      setShowCancelConfirm(false)
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel subscription.')
    } finally {
      setCancelling(false)
    }
  }

  const tone = statusTone(billing)
  const periodEnd = billing?.currentPeriodEnd
    ? new Date(billing.currentPeriodEnd).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null

  const canCancel =
    !billing?.billingExempt &&
    !billing?.cancelAtPeriodEnd &&
    (billing?.subscriptionStatus === 'active' || billing?.subscriptionStatus === 'trialing')

  return (
    <>
      <ContentHeader title="Billing" />

      <div className="content-panel">
        {loading ? (
          <div className="payment-inline-alert">Loading billing status…</div>
        ) : error ? (
          <div className="payment-inline-alert error">{error}</div>
        ) : (
          <div className="billing-layout">
            <section className="card billing-status-card">
              <div className="payment-card-kicker">Subscription</div>

              {billing?.billingExempt ? (
                <div className="billing-status-row">
                  <div className="payment-status-badge success">Billing Exempt</div>
                </div>
              ) : (
                <>
                  <div className="billing-status-row">
                    <div className={`payment-status-badge ${tone}`}>
                      {billing?.hasCustomer ? statusLabel(billing.subscriptionStatus) : 'No subscription'}
                    </div>
                    {billing?.livemode === false && (
                      <span className="billing-mode-chip">Test mode</span>
                    )}
                  </div>

                  {billing?.cancelAtPeriodEnd && periodEnd && (
                    <div className="payment-inline-alert billing-cancel-warning">
                      Your subscription cancels on {periodEnd}. You'll retain access until then.
                    </div>
                  )}

                  <div className="billing-summary-grid">
                    <div className="billing-summary-item">
                      <span className="payment-status-label">Plan</span>
                      <span className="payment-status-value">Pro</span>
                    </div>
                    <div className="billing-summary-item">
                      <span className="payment-status-label">
                        {billing?.cancelAtPeriodEnd ? 'Cancels on' : 'Renews on'}
                      </span>
                      <span className="payment-status-value">{periodEnd ?? '—'}</span>
                    </div>
                  </div>

                  {/* Cancel section */}
                  {canCancel && !showCancelConfirm && (
                    <div className="billing-cancel-section">
                      <button
                        type="button"
                        className="billing-cancel-trigger"
                        onClick={() => setShowCancelConfirm(true)}
                      >
                        Cancel subscription
                      </button>
                    </div>
                  )}

                  {showCancelConfirm && (
                    <div className="billing-cancel-confirm">
                      <p className="billing-cancel-confirm-title">Cancel your subscription?</p>
                      <div className="billing-cancel-options">
                        <label className="billing-cancel-option">
                          <input
                            type="radio"
                            name="cancel-timing"
                            checked={!cancelImmediately}
                            onChange={() => setCancelImmediately(false)}
                          />
                          <div>
                            <span className="billing-cancel-option-label">At period end</span>
                            <span className="billing-cancel-option-desc">
                              Keep access until {periodEnd ?? 'end of billing period'}, then cancel.
                            </span>
                          </div>
                        </label>
                        <label className="billing-cancel-option">
                          <input
                            type="radio"
                            name="cancel-timing"
                            checked={cancelImmediately}
                            onChange={() => setCancelImmediately(true)}
                          />
                          <div>
                            <span className="billing-cancel-option-label">Immediately</span>
                            <span className="billing-cancel-option-desc">
                              Access ends right now. This cannot be undone.
                            </span>
                          </div>
                        </label>
                      </div>

                      {cancelError && (
                        <div className="payment-inline-alert error" aria-live="polite">{cancelError}</div>
                      )}

                      <div className="billing-cancel-confirm-actions">
                        <button
                          type="button"
                          className="billing-cancel-confirm-button"
                          onClick={handleCancelSubscription}
                          disabled={cancelling}
                        >
                          {cancelling ? 'Cancelling…' : 'Confirm cancellation'}
                        </button>
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => { setShowCancelConfirm(false); setCancelError(null) }}
                          disabled={cancelling}
                        >
                          Keep subscription
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </>
  )
}

// ─── Paywall ─────────────────────────────────────────────────────────────────

const PLAN_FEATURES = [
  'Unlimited calls with Bev',
  'Chat with Bev',
  'AI-powered call notes & summaries',
  'Automatic action item tracking',
  'Smart pre-call briefings',
  'Follow-up email drafts',
  'Contact management',
  'Calendar integration',
  'Catalog Connect',
]

function PaywallView({ onLogout }: { onLogout?: () => void }) {
  const posthog = usePostHog()
  const [prices, setPrices] = useState<StripePrice[]>([])
  const [pricesLoading, setPricesLoading] = useState(true)
  const [pricesError, setPricesError] = useState<string | null>(null)
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const successUrl = `${window.location.origin}/payments/success`
  const cancelUrl = `${window.location.origin}/payments/cancel`

  // Fetch prices for the configured product
  useEffect(() => {
    const productId = STRIPE_PRODUCT_IDS[0]
    if (!productId) {
      // Fall back to the static price ID if no product configured
      const fallback = STRIPE_PRICE_ID?.trim()
      if (fallback) {
        setPrices([{ price_id: fallback, product_id: '', currency: 'usd', unit_amount: null, interval: null, interval_count: null }])
        setSelectedPriceId(fallback)
      } else {
        setPricesError('No product or price configured.')
      }
      setPricesLoading(false)
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        const userId = await ensureSessionUserId(getSessionAuth())
        const data = (await getStripePrices(productId, userId ?? undefined)) as unknown as StripePrice[]
        const active = data
          .filter((p) => p.active !== false)
          .sort((a, b) => {
            const ia = intervalSortOrder[a.interval ?? ''] ?? 99
            const ib = intervalSortOrder[b.interval ?? ''] ?? 99
            return ia - ib
          })
        if (!cancelled) {
setPrices(active)
          setSelectedPriceId(active[0]?.price_id ?? null)
        }
      } catch (err) {
        if (!cancelled) setPricesError(err instanceof Error ? err.message : 'Failed to load prices.')
      } finally {
        if (!cancelled) setPricesLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const handleSubscribe = async () => {
    if (!selectedPriceId) {
      setErrorMessage('Please select a plan to continue.')
      return
    }

    setIsRedirecting(true)
    setErrorMessage(null)

    try {
      const userId = await ensureSessionUserId(getSessionAuth())
      if (!userId) throw new Error('Unable to identify your account. Please sign in again.')

      const payload = await createCheckoutSession({
        user_id: userId,
        price_id: selectedPriceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      })

      const checkoutUrl = (payload.checkoutUrl ?? payload.checkout_url)?.trim()
      if (!checkoutUrl) throw new Error('No checkout URL returned from server.')

      const selectedPrice = prices.find((p) => p.price_id === selectedPriceId)
      posthog?.capture('checkout_started', {
        price_id: selectedPriceId,
        interval: selectedPrice?.interval,
        trial_eligible: selectedPrice?.trial_eligible,
      })
      window.location.href = checkoutUrl
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      setIsRedirecting(false)
    }
  }

  // Derive price display for header: show selected price or first price
  const displayPrice = prices.find((p) => p.price_id === selectedPriceId) ?? prices[0] ?? null
  const trialEligible = displayPrice?.trial_eligible === true

  return (
    <div className="paywall-layout">
      {/* Brand */}
      <div className="paywall-brand-pill">
        <img src="/logo.svg" alt="" className="paywall-pill-logo" />
        <img src="/wordmark.png" alt="Scribbl" className="paywall-pill-wordmark" />
      </div>

      {/* Plan cards */}
      <div className="paywall-plans-grid">
        {/* Pro */}
        <div className="paywall-card">
          <div className="paywall-card-header">
            <div className="paywall-card-kicker-row">
              <div className="paywall-card-kicker">Pro plan</div>
              {trialEligible && (
                <div className="paywall-trial-pill">14-day free trial</div>
              )}
            </div>
            <div className="paywall-card-title">Everything you need</div>
            <div className="paywall-card-price">
              {pricesLoading ? (
                <span className="paywall-price-loading">Loading…</span>
              ) : displayPrice?.price_per_interval ? (
                <span className="paywall-price-formatted">{displayPrice.price_per_interval}</span>
              ) : displayPrice ? (
                <>
                  <span className="paywall-price-amount">{formatAmount(displayPrice)}</span>
                  <span className="paywall-price-period">{formatInterval(displayPrice)}</span>
                </>
              ) : null}
            </div>
          </div>

          <div className="paywall-card-body">
            <ul className="paywall-features">
              {PLAN_FEATURES.map((feature) => (
                <li key={feature} className="paywall-feature">
                  <span className="paywall-feature-check" aria-hidden="true">✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <hr className="paywall-divider" />

            {/* Price selector — only shown when multiple prices exist */}
            {!pricesLoading && !pricesError && prices.length > 1 && (
              <div className="paywall-price-selector">
                {prices.map((price) => (
                  <button
                    type="button"
                    key={price.price_id}
                    className={`paywall-price-option ${selectedPriceId === price.price_id ? 'selected' : ''}`}
                    onClick={() => setSelectedPriceId(price.price_id)}
                  >
                    <span className="paywall-price-option-label">
                      {price.nickname ?? (price.interval === 'year' ? 'Annual' : price.interval === 'month' ? 'Monthly' : price.interval ?? 'One-time')}
                    </span>
                    <span className="paywall-price-option-amount">
                      {price.price_per_interval ?? `${formatAmount(price)}${formatInterval(price) ? ` ${formatInterval(price)}` : ''}`}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {pricesError ? (
              <div className="payment-inline-alert error" aria-live="polite">{pricesError}</div>
            ) : null}

            <button
              type="button"
              className="primary-button paywall-subscribe-button"
              onClick={handleSubscribe}
              disabled={isRedirecting || pricesLoading || !selectedPriceId}
            >
              {isRedirecting
                ? 'Redirecting to Stripe…'
                : trialEligible
                  ? 'Start 14-day free trial'
                  : 'Subscribe to Pro'}
            </button>

            {trialEligible && !isRedirecting && (
              <p className="paywall-trial-note">No charge until your trial ends. Cancel anytime.</p>
            )}

            {errorMessage ? (
              <div className="payment-inline-alert error" aria-live="polite">
                {errorMessage}
              </div>
            ) : null}

            <div className="paywall-trust-row">
              <svg className="paywall-lock-icon" width="11" height="13" viewBox="0 0 11 13" fill="none" aria-hidden="true">
                <rect x="0.75" y="5.75" width="9.5" height="6.5" rx="1.75" stroke="currentColor" strokeWidth="1.5" />
                <path d="M3.25 5.5V4a2.25 2.25 0 014.5 0v1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {trialEligible ? 'Secured by Stripe' : 'Secured by Stripe · Cancel anytime'}
            </div>
          </div>
        </div>

        {/* Enterprise */}
        <div className="paywall-card paywall-card--muted">
          <div className="paywall-card-header paywall-card-header--dark">
            <div className="paywall-card-kicker">Enterprise plan</div>
            <div className="paywall-card-title">Built for teams</div>
            <div className="paywall-card-price">
              <span className="paywall-coming-soon-badge">Coming Soon</span>
            </div>
          </div>

          <div className="paywall-card-body">
            <ul className="paywall-features paywall-features--muted">
              {PLAN_FEATURES.map((feature) => (
                <li key={feature} className="paywall-feature">
                  <span className="paywall-feature-check paywall-feature-check--muted" aria-hidden="true">✓</span>
                  {feature}
                </li>
              ))}
              <li className="paywall-feature">
                <span className="paywall-feature-check paywall-feature-check--muted" aria-hidden="true">✓</span>
                Team management & admin controls
              </li>
              <li className="paywall-feature">
                <span className="paywall-feature-check paywall-feature-check--muted" aria-hidden="true">✓</span>
                Priority support & SLA
              </li>
            </ul>

            <hr className="paywall-divider" />

            <button type="button" className="paywall-enterprise-button" disabled>
              Coming Soon
            </button>
          </div>
        </div>
      </div>

      {onLogout ? (
        <button type="button" className="paywall-signout-button" onClick={onLogout}>
          Sign out
        </button>
      ) : null}

    </div>
  )
}

// ─── Export ──────────────────────────────────────────────────────────────────

function PaymentsView({ variant = 'paywall', onLogout }: PaymentsViewProps) {
  if (variant === 'billing') return <BillingView />
  return <PaywallView onLogout={onLogout} />
}

export default PaymentsView
